import { cva, type VariantProps } from 'class-variance-authority';

const chipSharedClasses = [
  '!rounded-full',
  'hover:!scale-100',
  'focus:!shadow-none focus-visible:shadow-ui-control',
  'active:shadow-none',
  '!ring-0',
  'max-[420px]:!px-2 max-[420px]:!py-[0.32rem] max-[420px]:!text-[0.76rem]',
].join(' ');

const chipPressedClasses = [
  '[&.is-pressed]:ring-0',
  '[&.is-pressed]:shadow-none',
  '[&.is-pressed]:hover:shadow-none',
  '[&.is-pressed]:hover:!scale-100',
].join(' ');

export const buttonVariants = cva(
  [
    'flex items-center justify-center gap-2',
    'rounded-full',
    'cursor-pointer',
    'transition-transform duration-200 ease-in-out',
    'hover:scale-[1.033] hover:opacity-90',
    'hover:shadow-ui-control',
    'focus:shadow-ui-control',
    'focus:outline-none',
    'disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 disabled:opacity-100 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:bg-gray-200 disabled:hover:text-gray-400 disabled:hover:shadow-none',
    '[&.is-pressed]:opacity-100 [&.is-pressed]:ring-2 [&.is-pressed]:ring-white/85',
  ].join(' '),
  {
    variants: {
      variant: {
        primary: 'bg-ui-primary text-ui-primary-foreground border border-ui-primary-border',
        muted: 'bg-ui-muted text-ui-muted-foreground border border-ui-primary-border',
        outline: 'border border-ui-outline-border text-ui-outline-foreground',
        ghost: 'bg-transparent text-ui-primary border border-ui-primary-border',
        chip: [
          'border border-(--docs-border)',
          'bg-(--docs-surface)',
          'text-(--docs-text-main)',
          'hover:border-(--docs-accent)',
          chipSharedClasses,
          '[&.is-pressed]:bg-(--docs-surface-raised)',
          '[&.is-pressed]:border-(--docs-border-soft)',
          '[&.is-pressed]:text-(--docs-accent-strong)',
          chipPressedClasses,
        ].join(' '),
        'chip-danger': [
          'border border-(--docs-danger-strong)',
          'bg-(--docs-danger-soft)',
          'text-(--docs-danger-strong)',
          'hover:border-(--docs-danger-strong)',
          chipSharedClasses,
          '[&.is-pressed]:bg-(--docs-danger-soft)',
          '[&.is-pressed]:border-(--docs-danger-strong)',
          '[&.is-pressed]:text-(--docs-danger-strong)',
          chipPressedClasses,
        ].join(' '),
        'docs-primary':
          'bg-(--docs-accent) text-white border border-(--docs-accent) hover:bg-(--docs-accent-strong) hover:border-(--docs-accent-strong)',
        'docs-secondary':
          'bg-white text-(--docs-accent-strong) border border-(--docs-accent-soft) hover:bg-white hover:text-(--docs-accent-strong)',
      },
      size: {
        xs: 'h-8 px-3 text-xs',
        sm: 'h-10 px-4',
        lg: 'h-11 px-6',
        chip: '!h-auto !px-[0.62rem] !py-[0.35rem] !text-[0.78rem]',
        icon: '!h-[1.9rem] !w-[1.9rem] !p-0 shrink-0 inline-flex',
      },
    },
    compoundVariants: [
      {
        variant: ['chip', 'chip-danger'],
        size: 'icon',
        class: ['!rounded-[var(--docs-radius-icon)]', 'max-[420px]:!p-0'].join(' '),
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
    },
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
